import React, { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { PROFILES_ADMIN_PREFIX, USER_ADMIN_PREFIX } from '../../../utils/constants';
import { displayPhoneNumber } from '../../../utils/formatters';
import { getComputedCSSVariable } from '../../../utils/cssVariableHelpers';
import { SellerProfile } from './interfaces';
import OpenSVG from './assets/OpenSVG';

const useSortableData = (items: Array<SellerProfile>, config = null) => {
  const [sortConfig, setSortConfig] = useState<{key: string, direction: string}|null>(config);

  const sortedItems = useMemo(() => {
    const sortableItems = [...items];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [items, sortConfig]);

  const requestSort = (key: string) => {
    const direction = (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc')
      ? 'desc'
      : 'asc';
    setSortConfig({ key, direction });
  };

  return { items: sortedItems, requestSort, sortConfig };
};

interface Props {
  accountID: string,
  profiles: Array<SellerProfile>,
  totalProfilesCount: number,
}

const ProfilesSubTable: React.FC<Props> = ({
  profiles,
  accountID,
  totalProfilesCount,
}) => {
  const { items, requestSort, sortConfig } = useSortableData(profiles);

  const getCurrentSort = (name: string) => (sortConfig && sortConfig?.key === name ? sortConfig.direction : '');

  return (
    <div className="table-responsive-md">
      <table className="table">
        <thead className="bg-white">
          <tr>
            <th className="sort-wrapper align-middle border-0">
              <a
                href="#"
                className="btn text-table-header-text border-none d-flex container h-100 align-items-center p-0"
                onClick={(e) => {
                  e.preventDefault();
                  requestSort('businessName');
                }}
              >
                <span>
                  <p className="m-0 mr-1 text-uppercase">Profile Name</p>
                </span>
                <span className="d-flex flex-column justify-content-center align-items-center position-relative">
                  <FontAwesomeIcon
                    size="xs"
                    icon={faSortUp}
                    color={getCurrentSort('businessName') === 'asc' ? getComputedCSSVariable('black') : getComputedCSSVariable('table-header-text')}
                  />
                  <FontAwesomeIcon
                    size="xs"
                    icon={faSortDown}
                    color={getCurrentSort('businessName') === 'desc' ? getComputedCSSVariable('black') : getComputedCSSVariable('table-header-text')}
                  />
                </span>
              </a>
            </th>
            <th className="sort-wrapper align-middle border-0">
              <a
                href="#"
                className="btn text-table-header-text border-none d-flex container h-100 align-items-center p-0"
                onClick={(e) => {
                  e.preventDefault();
                  requestSort('primary_contact_name');
                }}
              >
                <span>
                  <p className="m-0 mr-1 text-uppercase">Primary Contact</p>
                </span>
                <span className="d-flex flex-column justify-content-center align-items-center position-relative">
                  <FontAwesomeIcon
                    size="xs"
                    icon={faSortUp}
                    color={getCurrentSort('primary_contact_name') === 'asc' ? getComputedCSSVariable('black') : getComputedCSSVariable('table-header-text')}
                  />
                  <FontAwesomeIcon
                    size="xs"
                    icon={faSortDown}
                    color={getCurrentSort('primary_contact_name') === 'desc' ? getComputedCSSVariable('black') : getComputedCSSVariable('table-header-text')}
                  />
                </span>
              </a>
            </th>
            <th className="sort-wrapper align-middle border-0">
              <a
                href="#"
                className="btn text-table-header-text border-none d-flex container h-100 align-items-center p-0"
                onClick={(e) => {
                  e.preventDefault();
                  requestSort('phone');
                }}
              >
                <span>
                  <p className="m-0 mr-1 text-uppercase">Phone</p>
                </span>
                <span className="d-flex flex-column justify-content-center align-items-center position-relative">
                  <FontAwesomeIcon
                    size="xs"
                    icon={faSortUp}
                    color={getCurrentSort('phone') === 'asc' ? getComputedCSSVariable('black') : getComputedCSSVariable('table-header-text')}
                  />
                  <FontAwesomeIcon
                    size="xs"
                    icon={faSortDown}
                    color={getCurrentSort('phone') === 'desc' ? getComputedCSSVariable('black') : getComputedCSSVariable('table-header-text')}
                  />
                </span>
              </a>
            </th>
            <th className="sort-wrapper align-middle border-0">
              <a
                href="#"
                className="btn text-table-header-text border-none d-flex container h-100 align-items-center p-0"
                onClick={(e) => {
                  e.preventDefault();
                  requestSort('best_time_to_call');
                }}
              >
                <span>
                  <p className="m-0 mr-1 text-uppercase">Best Time to Call</p>
                </span>
                <span className="d-flex flex-column justify-content-center align-items-center position-relative">
                  <FontAwesomeIcon
                    size="xs"
                    icon={faSortUp}
                    color={getCurrentSort('best_time_to_call') === 'asc' ? '#000000' : '#888888'}
                  />
                  <FontAwesomeIcon
                    size="xs"
                    icon={faSortDown}
                    color={getCurrentSort('best_time_to_call') === 'desc' ? '#000000' : '#888888'}
                  />
                </span>
              </a>
            </th>
          </tr>
        </thead>
        <tbody>
          {
            items.map((profile: SellerProfile) => (
              <tr key={`${accountID}-profile-${profile.id}`}>
                <td className="border-left-0 border-right-0">
                  <a
                    className="d-flex align-items-center m-0 text-dark-btn-txt font-weight-bold"
                    href={`${PROFILES_ADMIN_PREFIX}/${profile.id}`}
                  >
                    <span className="mr-4">
                      {profile.businessName}
                    </span>
                    <OpenSVG style={{ width: 10, height: 10 }} />
                  </a>
                </td>
                <td className="border-left-0 border-right-0">
                  <p className="text-dark-btn-txt m-0">{profile.primary_contact_name}</p>
                </td>
                <td className="border-left-0 border-right-0">
                  {!!profile?.phone && (
                    <p className="text-nowrap text-table-light-gray m-0">
                      {displayPhoneNumber(profile.phone as string)}
                      {profile.phone_extn ? `ext. ${profile.phone_extn}` : ''}
                    </p>
                  )}
                </td>
                <td className="border-left-0 border-right-0">
                  <p className="text-table-light-gray m-0">{profile.best_time_to_call}</p>
                </td>
              </tr>
            ))
          }
        </tbody>
      </table>
      {(!!items && (items.length < totalProfilesCount)) && (
        <p className="text-center bg-table-header-gray text-dark-btn-txt mb-0 py-3">
          {`+ ${totalProfilesCount - items.length} more profiles. `}
          <a
            className="text-table-light-gray m-0"
            href={`${USER_ADMIN_PREFIX}/${accountID}`}
          >
            See all profiles for this user.
          </a>
        </p>
      )}
    </div>
  );
};

export default ProfilesSubTable;
